import { useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { ScanBarcode, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Barcode128 from "@/lib/barcode";
import { codigoArticulo, mismoFolio, resolverCodigoNumerico } from "@/components/traspasos/EtiquetasPedido";

/** Formato del código de la etiqueta de envío (CEDIS / RubiNova): FOLIO|SKU|CANTIDAD  ·  también acepta SKU|CANTIDAD y SKU */
export function parseCodigoEnvio(raw) {
  const partes = String(raw || "").trim().toUpperCase().split(/[|;]/).map((x) => x.trim()).filter(Boolean);
  if (!partes.length) return null;
  if (partes.length >= 3) return { folio: partes[0], sku: partes[1], cantidad: parseFloat(partes[2].replace(",", ".")) || null };
  if (partes.length === 2) {
    if (/^\d+$/.test(partes[0]) && !/^[\d.,]+$/.test(partes[1])) return { folio: partes[0], sku: partes[1], cantidad: null }; // código corto N|SKU
    return { folio: null, sku: partes[0], cantidad: parseFloat(partes[1].replace(",", ".")) || null };
  }
  return { folio: null, sku: partes[0], cantidad: null };
}

export function EscanerRecepcion({ recibir, setRecibir }) {
  const [codigo, setCodigo] = useState("");
  const [ultimo, setUltimo] = useState(null);
  const ref = useRef(null);

  function escanear(e) {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const p = resolverCodigoNumerico(codigo, recibir) || parseCodigoEnvio(codigo);
    setCodigo("");
    if (!p) return;
    if (p.folio && !mismoFolio(p.folio, recibir.folio)) { toast.error(`Ese bulto es del pedido ${p.folio}, no de ${recibir.folio}`); return; }
    const idx = recibir.items.findIndex((i) => (i.sku || "").toUpperCase() === p.sku || (i.codigo_barras || "").toUpperCase() === p.sku);
    if (idx < 0) { toast.error(`${p.sku} no viene en este pedido`); setUltimo({ ok: false, sku: p.sku }); return; }
    const it = recibir.items[idx];
    const suma = p.cantidad ?? (["pza", "pzas", "pz"].includes((it.unidad || "").toLowerCase()) ? 1 : it.cantidad_enviada);
    const nuevo = Math.round(((parseFloat(it.cantidad_recibida) || 0) + suma) * 1000) / 1000;
    setRecibir({ ...recibir, items: recibir.items.map((x, j) => (j === idx ? { ...x, cantidad_recibida: nuevo } : x)) });
    setUltimo({ ok: true, sku: it.sku, nombre: it.nombre, suma, unidad: it.unidad, total: nuevo, enviado: it.cantidad_enviada });
    ref.current?.focus();
  }

  const faltan = recibir.items.filter((i) => (parseFloat(i.cantidad_recibida) || 0) < i.cantidad_enviada);
  const sobran = recibir.items.filter((i) => (parseFloat(i.cantidad_recibida) || 0) > i.cantidad_enviada);
  return (
    <div className="rounded-2xl border-2 border-brand-ink bg-brand-ink text-white p-3 space-y-2" data-testid="escaner-recepcion">
      <div className="flex items-center gap-2">
        <ScanBarcode className="w-5 h-5 text-brand-amber shrink-0" />
        <input ref={ref} autoFocus data-testid="escaner-input" value={codigo} onChange={(e) => setCodigo(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") escanear(e); }} placeholder="Escanea la etiqueta del bulto (FOLIO|SKU|CANTIDAD)" className="field h-10 flex-1 font-mono text-sm text-brand-ink" />
        <button type="button" data-testid="escaner-submit" onClick={escanear} className="btn-amber h-10 px-3 text-xs">Sumar</button>
      </div>
      {ultimo && (ultimo.ok
        ? <div className="text-xs flex items-center gap-1.5 text-emerald-300" data-testid="escaner-ultimo"><CheckCircle2 className="w-3.5 h-3.5" /> {ultimo.nombre}: +{ultimo.suma} {ultimo.unidad} → {ultimo.total} / {ultimo.enviado} {ultimo.unidad}</div>
        : <div className="text-xs flex items-center gap-1.5 text-rose-300" data-testid="escaner-ultimo"><AlertTriangle className="w-3.5 h-3.5" /> {ultimo.sku} no pertenece al pedido</div>)}
      <div className="flex flex-wrap gap-1.5 text-[10px]" data-testid="escaner-diferencias">
        {faltan.length === 0 && sobran.length === 0 ? <span className="chip chip-emerald">Sin diferencias · todo recibido</span> : <>
          {faltan.map((i) => <span key={i.componente_id} className="chip chip-red font-mono">Falta {i.nombre}: {Math.round((i.cantidad_enviada - (parseFloat(i.cantidad_recibida) || 0)) * 1000) / 1000} {i.unidad}</span>)}
          {sobran.map((i) => <span key={i.componente_id} className="chip chip-amber font-mono">Sobra {i.nombre}: {Math.round(((parseFloat(i.cantidad_recibida) || 0) - i.cantidad_enviada) * 1000) / 1000} {i.unidad}</span>)}
        </>}
      </div>
    </div>
  );
}

/** Etiquetas de envío (Zebra 40×13 mm) para que el CEDIS / RubiNova marque cada bulto con FOLIO|SKU|CANTIDAD. */
export function imprimirEtiquetasEnvio(t) {
  const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const etiquetas = t.items.map((i) => {
    const code = codigoArticulo(t, i);
    return `<div class="et"><div class="n">${esc(i.nombre)}</div><div class="s">${esc(t.folio)} · ${esc(i.sku)} · <b>${i.cantidad_enviada} ${esc(i.unidad)}</b> → ${esc(t.destino_nombre)}</div><div class="bc">${renderToStaticMarkup(<Barcode128 value={code} height={10} />)}</div><div class="c">${esc(code)}</div></div>`;
  }).join("");
  const w = window.open("", "_blank", "width=520,height=720");
  if (!w) return false;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Etiquetas ${esc(t.folio)}</title><style>@page{size:40mm 13mm;margin:0}*{box-sizing:border-box}body{margin:0;font-family:Arial,Helvetica,sans-serif;color:#000}.et{width:40mm;height:13mm;padding:.6mm 1.2mm .4mm;page-break-after:always;display:flex;flex-direction:column;justify-content:space-between;overflow:hidden}.n{font-size:6.5pt;font-weight:700;text-transform:uppercase;line-height:1.05;max-height:2.2em;overflow:hidden}.s{font-size:5pt;line-height:1.1}.bc svg{width:100%;height:4.2mm;display:block}.c{font-family:monospace;font-size:4.5pt;text-align:center;letter-spacing:.02em}</style></head><body>${etiquetas}<script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`);
  w.document.close();
  return true;
}
